import { useState, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * Custom hook for managing message-related functionality
 * Handles message sending, auto-forward, and request review
 */
export const useMessageHandling = (conversationState, token, currentMode = null) => {
  // Message handling state
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [autoForwardEnabled, setAutoForwardEnabled] = useState(false);

  // Refs
  const userIsNearBottomRef = useRef(true); // Start at bottom
  const messageContainerRef = useRef();

  // Message sending handler
  const handleTherapistSendMessage = useCallback(async (messageText, wasAiSuggestionRejected = false, discardedSuggestion = null) => {
    if (!messageText?.trim() || !conversationState.selectedConversation || isSending) return;

    setIsSending(true);
    setSendError('');

    try {
      const messageData = {
        message: messageText,
        conversationId: conversationState.selectedConversation._id,
        senderRole: 'human_therapist'
      };
      
      // Add assisted mode metadata for manual messages in assisted mode
      if (currentMode === 'assisted') {
        messageData.sentInMode = 'assisted';
        // In assisted mode, any message sent without using AI suggestion is manual
        // wasAiSuggestionRejected is true when suggestion was explicitly rejected
        // but we also want to mark as manual when no suggestion was shown
        messageData.aiSuggestionHandling = wasAiSuggestionRejected ? 'REJECTED_MANUAL' : 'MANUAL';
        // Keep the discarded draft so a rejected suggestion leaves a record (31 Oct 2026).
        if (wasAiSuggestionRejected && discardedSuggestion) messageData.aiSuggestionOriginalText = discardedSuggestion;
      }
      
      await api.sendMessage(messageData, token);
      return true; // Success
    } catch (err) {
      console.error('Error sending therapist message:', err);
      setSendError(err.message || 'Failed to send message');
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [conversationState, isSending, token, currentMode]);

  // Auto-forward toggle handler
  const handleToggleAutoForward = useCallback(async (enabled) => {
    if (!conversationState.selectedConversation) return;

    try {
      await api.toggleAutoForward(conversationState.selectedConversation._id, enabled, token);
      setAutoForwardEnabled(enabled);
      conversationState.setSelectedConversation(prev => ({ 
        ...prev, 
        autoForwardEnabled: enabled 
      }));
      return `Auto-forward ${enabled ? 'enabled' : 'disabled'}`;
    } catch (err) {
      console.error('Error toggling auto-forward:', err);
      throw err;
    }
  }, [conversationState, token]);

  // Request review handler
  const handleRequestReview = useCallback(async (conversationId, reviewerPersonaId = null) => {
    try {
      const result = await api.requestConversationReview(conversationId, token, reviewerPersonaId);
      return result.review; // Return the actual review data
    } catch (err) {
      console.error('Error requesting review:', err);
      throw err;
    }
  }, [token]);

  // Handle scroll events to track if user is near bottom
  const handleScroll = useCallback(() => {
    if (messageContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
      userIsNearBottomRef.current = isNearBottom;
    }
  }, []);

  // Socket event handlers
  const handleNewMessage = useCallback((data) => {
    conversationState.setSelectedConversation(prevConvo => {
      if (!prevConvo || !prevConvo.messages || !data.message || !data.message.senderRole) return prevConvo;
      const messageExists = prevConvo.messages.some(msg =>
        msg && (
          (data.message._id && msg._id === data.message._id) ||
          (msg.timestamp === data.message.timestamp && msg.text === data.message.text && msg.senderRole === data.message.senderRole)
        )
      );
      if (messageExists) return prevConvo;
      return { ...prevConvo, messages: [...prevConvo.messages, data.message] };
    });
  }, [conversationState]);

  const handleModeChange = useCallback((data) => {
    conversationState.setSelectedConversation(prev => ({ 
      ...prev, 
      controlMode: data.newMode, 
      status: data.status || prev.status 
    }));
    if (data.systemMessage && data.systemMessage.senderRole) {
      conversationState.setSelectedConversation(prevConvo => {
        if (!prevConvo || !prevConvo.messages) return prevConvo;
        return { ...prevConvo, messages: [...prevConvo.messages, data.systemMessage] };
      });
    }
  }, [conversationState]);

  const handleClientGoalActionUpdate = useCallback((data) => {
    if (data.systemMessage && data.systemMessage.senderRole) {
      conversationState.setSelectedConversation(prevConvo => {
        if (!prevConvo || !prevConvo.messages) return prevConvo;
        return { ...prevConvo, messages: [...prevConvo.messages, data.systemMessage] };
      });
    }
  }, [conversationState]);

  return {
    // State
    isSending,
    sendError,
    autoForwardEnabled,
    
    // Refs
    messageContainerRef,
    userIsNearBottomRef,
    
    // Setters
    setSendError,
    setIsSending,
    setAutoForwardEnabled,
    
    // Handlers
    handleTherapistSendMessage,
    handleToggleAutoForward,
    handleRequestReview,
    handleScroll,
    handleNewMessage,
    handleModeChange,
    handleClientGoalActionUpdate
  };
};

export default useMessageHandling;